import { AddComment, Chat, ChevronLeft, List } from "@carbon/icons-react"
import { StyleSheet } from "react-native"
import { View } from "react-native-web"
import React, { useEffect, useState } from "react"
import { ModDashboardQueueGeneral } from "open-modules/zdf/component/moddashboard/moddashboardqueue"
import { ModDashboardUserProfile } from "open-modules/zdf/component/moddashboard/moddashboarduserprofile"
import { ModDashboardThreadsOverview } from "open-modules/zdf/component/moddashboard/moddashboardthreads"
import { useDatastore, useInstanceKey, usePersonaKey, usePersonaObject, useStructureKey } from "util/datastore"
import { HorizBox, HoverView, Pad, PadBox, WindowTitle } from "component/basics"
import { LoadingScreen, Banner } from "component/banner"
import { colorBorderPrimary, colorSurfacePrimary, colorSurfaceSecondary, colorTextPrimary, colorTextSecondary, colorTextWarning } from "component/color"
import { gotoInstance } from "util/navigate"
import { Heading, UtilityText } from "component/text"
import { SpacedArray } from "system/demo"
import { TextButton } from "component/button"
import { useConfig } from "util/features"
import { useInstanceParams } from "util/params"
import { useHasCapability } from "component/admin"
import { ToastRenderer } from "open-modules/zdf/component/toast"
import { Blocklist } from "open-modules/zdf/component/blocklist/blocklist"
import { Catcher } from "system/catcher"
import { getDeploymentConfig, hoverColor, pressColor } from "util/util"
import { UserInfo } from "component/topbar"
import { ModDashboardSettingsConfig } from "open-modules/zdf/feature/moddashboard-settings"
import { ChannelManagement } from "open-modules/zdf/component/channels/channel-management"
import { computedLabel, t } from "util/stdtypes"

export interface ModDashboardConfig {
    moderationPages: Array<{
        id: string;
        label: string;
        icon: React.ComponentType;
        component: React.ComponentType;
    }>;
    defaultModerationPage: {
        id: string;
        label: string;
        icon: React.ComponentType;
        component: React.ComponentType;
    };
}

export const ModDashboardStructure = {
    key: 'moddashboard',
    name: 'Moderation Dashboard',
    screen: ModDashboardHomeScreen,
    subscreens: {
    },
    receivesConfigDefaults: {
        shouldHideTopbar: true,
        openLinksInNewTab: true,
        moddashboardTopBarWidgets: [],
        moderationPages: [
            {
                id: "page-threads",
                label: "Threads",
                icon: AddComment,
                component: ModDashboardThreadsOverview,
            },
            {
                id: "page-comments",
                label: "Comments",
                icon: Chat,
                component: ModDashboardQueueGeneral
            },
            {
                id: "page-blocklist",
                label: "Blocklist",
                icon:List,
                component: Blocklist
            },
        ],
        defaultModerationPage: {
            id: "page-threads",
            label: "Threads",
            icon: AddComment,
            component: ModDashboardThreadsOverview
        }
    }
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function ModDashboardHomeScreen() {

    const config = useConfig() as ModDashboardConfig;
    const params = useInstanceParams();
    const personaKey = usePersonaKey();
    const canView = useHasCapability('moderation/view')
    const datastore = useDatastore();
    const deploymentConfig = getDeploymentConfig();

    if (!config || !Array.isArray(config.moderationPages) || !config.defaultModerationPage) {
        throw new Error("Invalid Mod Dashboard configuration");
    }

    const CurrentModerationPage = config.moderationPages.find((page) => (page.id === params.pageId))?.component ?? config.defaultModerationPage?.component;
    const isLoggedIn = personaKey !== undefined && personaKey !== null;

    useEffect(() => {
        // Redirecting to the login screen clashes with skipping it and automatically opening the login provider popup
        if (!isLoggedIn && !deploymentConfig.skipLoginProviderScreen) {
            datastore.gotoInstance({ structureKey: 'login', instanceKey: 'one' });
        }
    }, [isLoggedIn])

    if(!canView) {
        return <Banner><UtilityText label={t("You do not have access to this feature")}/></Banner>
    }

    function onClick(event) {
        // Top level click handler
        // Closes current mod task as long the click source is not from a modTask
        if (!event.isFromModTask) {
            datastore.setSessionData(['modTask-selected-idx'], null);
        }
        return false
    }

    return (
        <>
            <WindowTitle label={t("Moderation Dashboard")} />
            <View style={{ flexDirection: "row", flexGrow: 1, width: "100%" }} onClick={onClick}>
                <ModDashboardSidebar />
                <View style={{ flexGrow: 1, flexShrink: 1 }}>
                    <ModerationHeader />
                    <View style={{ height: "calc(100vh - 80px)", overflow: "hidden", backgroundColor: colorSurfaceSecondary }}>
                        {/* User profile can be opened from anywhere is layed on top of the current first level content */}
                        {params?.userId && <View style={{ overflow: "auto", height: "100%", paddingHorizontal: 104 }}>
                            <ModDashboardUserProfile key={params?.userId} userId={params?.userId} />
                        </View>
                        }
                        {/* When user profile is open hide the previous content, to not reset their state */}
                        <View style={{ overflow: "auto", height: "100%", visibility: params?.userId ? "hidden" : "visible", position: params?.userId ? "absolute" : "static", paddingHorizontal: 104 }}>
                            {CurrentModerationPage ? <CurrentModerationPage /> : <LoadingScreen />}
                        </View>
                    </View>
                    <View style={{ width: "100%", bottom: "5%", position: "absolute", alignItems: "center" }}>
                        <ToastRenderer />
                    </View>
                </View>
            </View>
        </>
    );
}

const modDashboardSidebarStyle = StyleSheet.create({
    sideBar: {
        display: "flex",
        flexDirection: "column",
        margin: 0,
        padding: 40,
        borderRightWidth: 1,
        borderRightColor: colorBorderPrimary,
        borderRadius: 1,
        borderStyle: "solid",
        justifyContent: "space-between",
        backgroundColor: colorSurfacePrimary
    },
});

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function ModDashboardSidebar() {
    const datastore = useDatastore();

    const personaKey = usePersonaKey();
    const config = useConfig() as ModDashboardConfig;
    const deploymentConfig = getDeploymentConfig();
    const psiBrandName = deploymentConfig.psiBrandName ?? "PSI";
    const persona = usePersonaObject(personaKey);
    const isLoggedIn = personaKey !== undefined && personaKey !== null;
    const params = useInstanceParams();
    const instanceKey = useInstanceKey()
    const pageId = params?.pageId ?? config.defaultModerationPage.id;
    const conEditBlocklist = useHasCapability('moderation/edit-blocklist');
    const conViewBlocklist = useHasCapability('moderation/view-blocklist');

    const canViewBlocklist = conEditBlocklist || conViewBlocklist;

    const filteredPages = config.moderationPages.filter(page => {
        return !(page.id === "page-blocklist" && !canViewBlocklist);
    });

    const switchPage = (pageId) => {
        gotoInstance({ structureKey: "moddashboard", instanceKey, params: { pageId } })
    };

    const [isReduced, setIsReduced] = useState(false);
        
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= window.screen.availWidth / 1.3) {
                setIsReduced(true);
            } else {
                setIsReduced(false);
            }
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const s = modDashboardSidebarStyle;

    const dynamicStyles = {
        width: isReduced ? "64px" : "fit-content",
        paddingHorizontal: isReduced ? 8 : 40,
    };

    return (
        <View style={{ ...s.sideBar, ...dynamicStyles }}>
            <View>
                {!isReduced && <Heading label={computedLabel("{psiBrandName} Moderation")} formatParams={{psiBrandName}} type="small" />}
                {isReduced && <Pad size={52} />}
                <Pad size={40} />
                <SpacedArray pad={16}>
                    {filteredPages.map((page, i) => (
                        <ModDashboardSidebarNavigationButton
                            key={"tab-" + i}
                            icon={page.icon}
                            label={!isReduced ? page.label : null}
                            onPress={() => { switchPage(page.id) }}
                            selected={pageId === page.id}
                            isReduced={isReduced}
                        />
                    ))
                    }
                </SpacedArray>
            </View>
            <HorizBox center>
                <UserInfo compact={isReduced}/>
            </HorizBox>
        </View>
    );
}

export function ModerationSettingsPage() {
    const config = useConfig() as ModDashboardSettingsConfig;
    return (
        <PadBox vert={80}>
            <Heading type="large" label={t("Settings")} weight="medium" />
            <Pad size={26} />
            {config.channelSettingsEnabled && <ChannelManagement />}
        </PadBox>
    );
}

const ModDashboardSidebarNavigationButtonStyle = StyleSheet.create({
    tile: {
        width: 220,
        height: 48,
        borderRadius: 32,
        paddingHorizontal: 12,
        // userSelect: "none",
        justifyContent: "center"
    },
    iconWrapper: {
        width: 48,
        height: 48,
        justifyContent: "center",
        alignItems: "center",
    },
    hover: {
        backgroundColor: hoverColor(colorSurfacePrimary)
    },
    selected: {
        backgroundColor: colorSurfaceSecondary
    },
    press: {
        backgroundColor: pressColor(colorSurfacePrimary)
    },
    notificationBubble: {
        backgroundColor: colorTextWarning,
        width: 8,
        height: 8,
        borderRadius: 4,
    },
});

function ModDashboardSidebarNavigationButton({ icon, label, onPress, showNotification = false, selected = false, isReduced = false }) {

    const s = ModDashboardSidebarNavigationButtonStyle

    const dynamicStyles = {
        width: isReduced ? "48px" : "220px",
        paddingHorizontal: isReduced ? 0 : 12,
    };

    return (
        <View>
            <HoverView
                style={[s.tile, selected ? s.selected : null, dynamicStyles ]}
                hoverStyle={s.hover}
                pressedStyle={s.press}
                onPress={onPress}
            >
                <View>
                    <HorizBox center>
                        <View style={s.iconWrapper}>
                            {React.createElement(icon, { width: 30, height: 30, color: colorTextPrimary })}
                        </View>
                        <Pad size={8} />
                        <UtilityText center label={label} />
                        <Pad size={8} />
                        <View style={showNotification ? s.notificationBubble : null}></View>
                    </HorizBox>
                </View>
            </HoverView>
        </View>
    );
}


// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
function ModerationHeader() {
    const params = useInstanceParams()
    const subPageOpen = params?.threadId || params?.userId

    const structureKey = useStructureKey()
    const instanceKey = useInstanceKey();

    const getBackButtonLabel = () => {
        if (params?.modTaskTreeKey || (params?.threadId && params?.userId)) {
            return t("Back to thread");
        }
        if (params?.threadId) {
            return t("Back to threads");
        }
        return t("Back to comments");
    };

    const backButtonLabel = getBackButtonLabel();

    const clearNavigationParams = (params) => {
        const newParams = { ...params };

        if (newParams.userId && newParams.modTaskTreeKey) {
            newParams.userId = null;
        } else if (newParams.userId) {
            newParams.userId = null;
            newParams.modTaskTreeKey = null;
        } else if (newParams.modTaskTreeKey && newParams.threadId) {
            newParams.modTaskTreeKey = null;
        } else {
            newParams.userId = null;
            newParams.threadId = null;
            newParams.modTaskTreeKey = null;
        }

        return newParams;
    }

    const goBack = () => {
        const newParams = clearNavigationParams(params);
        gotoInstance({ structureKey, instanceKey, params: newParams });
    }
    const {moddashboardTopBarWidgets} = useConfig<any>() 

    return <View style={{ height: 80, borderBottomWidth: 1, borderColor: colorBorderPrimary, justifyContent: "center", paddingLeft: 100, paddingRight: 40, backgroundColor: colorSurfacePrimary }}>
        <HorizBox spread center>
            {subPageOpen ? <TextButton leftIcon={ChevronLeft} label={backButtonLabel} color={colorTextSecondary} type="tiny" onPress={goBack} /> : <View></View>}
            {moddashboardTopBarWidgets.map((Widget,index)=><Catcher key={"catcher-"+index}><Widget key={index}/></Catcher>)}
        </HorizBox>
    </View>
}
